import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowRight, CheckCircle2, Send,
  Eye, Users, Target, ClipboardCheck,
  ShieldCheck, Wallet, LineChart, MapPin,
  Heart, Truck, Database, Mail, Code, FileSpreadsheet,
  Activity, TrendingUp, Building2, Network, Compass, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const apiClient = axios.create({ baseURL: window.location.origin + "/api" });

export default function LandingPage() {
  const navigate = useNavigate();
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [intent, setIntent] = useState("demo"); // demo | pilot
  const [form, setForm] = useState({ name: "", email: "", phone: "", organisationName: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const openIntent = (i) => { setIntent(i); setShowEnquiry(true); };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      await apiClient.post("/enquiries", {
        ...form, source: `landing_page_${intent}`,
        message: `[${intent.toUpperCase()}] ${form.message}`,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit enquiry");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased selection:bg-emerald-200/60">
      {/* ───────────── Nav ───────────── */}
      <nav className="fixed top-0 inset-x-0 bg-white/85 backdrop-blur-md border-b border-slate-200/80 z-50" data-testid="nav">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-[#15803D] flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm">E</span>
            </div>
            <span className="font-semibold tracking-tight">Engage</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">
              Login
            </Button>
            <Button onClick={() => openIntent("demo")} data-testid="nav-book-demo-btn"
              className="bg-[#15803D] hover:bg-[#166534] text-white">
              Book A Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* ───────────── HERO ───────────── */}
      <section className="pt-32 pb-24 px-5 sm:px-6" data-testid="hero">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-800 mb-6">
            <Eye className="h-3.5 w-3.5" /> Operational Visibility For Fundraising Organisations
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Run Your Entire Fundraising Operation From One System
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Manage campaigns, fundraisers, compliance, donor acquisition, Gift Aid,
            reporting and charity data delivery from a single platform.
          </p>
          <div className="max-w-2xl mx-auto bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-8 text-left">
            <p className="text-slate-700 text-base leading-relaxed">
              <span className="text-slate-500">Most fundraising systems show results.</span><br/>
              <strong>Engage shows everything that created those results.</strong><br/>
              <span className="text-slate-500">Track every fundraiser, every donor, every campaign and every outcome in one place.</span>
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Button onClick={() => openIntent("demo")} data-testid="hero-book-demo-btn"
              className="bg-[#15803D] hover:bg-[#166534] text-white px-7 py-6 text-base font-semibold">
              Book A Demo <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button onClick={() => openIntent("pilot")} variant="outline" data-testid="hero-request-pilot-btn"
              className="px-7 py-6 text-base font-semibold border-slate-300">
              Request A Pilot
            </Button>
          </div>
        </div>
      </section>

      {/* ───────────── SECTION 1 ───────────── */}
      <section className="py-20 px-5 sm:px-6 bg-slate-50 border-y border-slate-200" data-testid="section-three-pillars">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center tracking-tight mb-16">
            Fundraising Operations Are More Than Donor Records
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: "Campaigns",
                text: "Plan, monitor and optimise fundraising activity across teams, locations and initiatives." },
              { icon: Users, title: "People",
                text: "Manage onboarding, compliance, readiness and deployment of fundraisers." },
              { icon: Heart, title: "Donors",
                text: "Capture, track and deliver donor information while maintaining complete visibility of donation activity." },
            ].map((p, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-md transition-shadow"
                data-testid={`pillar-${p.title.toLowerCase()}`}>
                <div className="w-11 h-11 bg-emerald-50 rounded-lg flex items-center justify-center mb-4">
                  <p.icon className="h-5 w-5 text-[#15803D]" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2">{p.title}</h3>
                <p className="text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── SECTION 2 — Journey ───────────── */}
      <section className="py-24 px-5 sm:px-6" data-testid="section-journey">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center tracking-tight mb-4">
            See The Complete Journey
          </h2>
          <p className="text-center text-slate-600 max-w-2xl mx-auto mb-14">
            Every action is connected. Understand what happened, who did it, where it happened and what happened next.
          </p>
          <div className="flex flex-col items-center gap-2">
            {[
              { label: "Campaign", icon: Target },
              { label: "Fundraiser", icon: Users },
              { label: "Assignment", icon: ClipboardCheck },
              { label: "Venue", icon: MapPin },
              { label: "Donor", icon: Heart },
              { label: "Donation", icon: Wallet },
              { label: "Gift Aid", icon: TrendingUp },
              { label: "Reporting", icon: LineChart },
            ].map((step, i, arr) => (
              <div key={step.label} className="w-full max-w-sm">
                <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-5 py-3.5 shadow-sm"
                  data-testid={`journey-step-${step.label.toLowerCase()}`}>
                  <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <step.icon className="h-4 w-4 text-[#15803D]" />
                  </div>
                  <span className="font-semibold text-slate-900">{step.label}</span>
                  <span className="ml-auto text-xs text-slate-400 font-mono">{String(i + 1).padStart(2, "0")}</span>
                </div>
                {i < arr.length - 1 && (
                  <div className="flex justify-center my-1">
                    <div className="w-px h-5 bg-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── SECTION 3 — Campaigns ───────────── */}
      <section className="py-20 px-5 sm:px-6 bg-slate-50 border-y border-slate-200" data-testid="section-campaigns">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center tracking-tight mb-14">
            Campaign Management Built For Real-World Teams
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { icon: Target, t: "Campaign Creation",
                d: "Create and manage campaigns with location, compliance and performance visibility." },
              { icon: ClipboardCheck, t: "Assignment Management",
                d: "Assign the right people to the right campaigns with readiness and compliance checks built in." },
              { icon: MapPin, t: "Venue Oversight",
                d: "Track venue notifications, permits and operational activity." },
              { icon: Activity, t: "Performance Monitoring",
                d: "Monitor fundraising activity across campaigns, teams and locations." },
            ].map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 flex gap-4 hover:shadow-md transition-shadow"
                data-testid={`feature-${f.t.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <f.icon className="h-5 w-5 text-[#15803D]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg mb-1">{f.t}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── SECTION 4 — Workforce Readiness ───────────── */}
      <section className="py-24 px-5 sm:px-6" data-testid="section-workforce">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-medium text-emerald-800 mb-5">
                <ShieldCheck className="h-3.5 w-3.5" /> Compliance built in
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                Workforce Readiness At A Glance
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Know who is ready to work before they arrive on site.
              </p>
              <div className="space-y-2">
                {[
                  "Faster onboarding",
                  "Reduced compliance risk",
                  "Better deployment decisions",
                  "Improved operational control",
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-slate-700"
                    data-testid={`workforce-benefit-${i}`}>
                    <CheckCircle2 className="h-5 w-5 text-[#15803D] flex-shrink-0" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">Capabilities</div>
              <div className="grid grid-cols-2 gap-3">
                {["DBS tracking", "Right To Work tracking", "Training records",
                  "Agreement management", "Readiness scoring", "Compliance visibility"
                ].map((c, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg px-3 py-3 text-sm font-medium text-slate-800"
                    data-testid={`workforce-cap-${i}`}>
                    {c}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── SECTION 5 — Gift Aid ───────────── */}
      <section className="py-20 px-5 sm:px-6 bg-slate-900 text-white" data-testid="section-gift-aid">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center tracking-tight mb-4">
            Gift Aid Without The Guesswork
          </h2>
          <p className="text-center text-slate-300 max-w-2xl mx-auto mb-12">
            Track Gift Aid opportunities, declarations, claims and recovery performance.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
            {[
              ["Recoverable", "good"],
              ["Submitted", "neutral"],
              ["Paid", "good"],
              ["Outstanding", "warn"],
              ["Opportunity Lost", "bad"],
            ].map(([label, tone], i) => (
              <div key={label} className="bg-slate-800/60 border border-slate-700 rounded-xl p-4"
                data-testid={`ga-metric-${i}`}>
                <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${
                  tone === "good" ? "text-emerald-400" :
                  tone === "warn" ? "text-amber-400" :
                  tone === "bad" ? "text-rose-400" : "text-slate-400"
                }`}>{label}</div>
                <div className="text-2xl font-bold">£</div>
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 text-center font-semibold mb-3">Breakdowns</div>
            <div className="flex flex-wrap justify-center gap-2">
              {["By Campaign", "By Fundraiser", "By Location", "By Organisation"].map(b => (
                <span key={b} className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-full text-sm">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── SECTION 6 — Donor ───────────── */}
      <section className="py-24 px-5 sm:px-6" data-testid="section-donor">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Every Donor In One Place
          </h2>
          <p className="text-slate-600 text-lg mb-12 max-w-2xl mx-auto">
            Track donation history, Gift Aid activity and delivery status through a single donor record.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["Donation history", Wallet],
              ["Gift Aid declarations", TrendingUp],
              ["Payment activity", Activity],
              ["Delivery history", Truck],
              ["Supporter visibility", Eye],
            ].map(([t, Ic], i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:bg-white hover:shadow-md transition-all"
                data-testid={`donor-cap-${i}`}>
                <Ic className="h-5 w-5 text-[#15803D] mx-auto mb-2" />
                <div className="text-sm font-medium text-slate-800">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── SECTION 7 — Delivery ───────────── */}
      <section className="py-20 px-5 sm:px-6 bg-slate-50 border-y border-slate-200" data-testid="section-delivery">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center tracking-tight mb-4">
            Deliver Donor Data Your Way
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Share supporter information with charities through the method that works for them.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            {[
              ["Dashboard access", Eye],
              ["CSV export", FileSpreadsheet],
              ["Email delivery", Mail],
              ["Webhook delivery", Network],
              ["API delivery", Code],
            ].map(([t, Ic], i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 text-center"
                data-testid={`delivery-method-${i}`}>
                <Ic className="h-5 w-5 text-[#15803D] mx-auto mb-2" />
                <div className="text-sm font-semibold">{t}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm">
            <CheckCircle2 className="h-4 w-4 text-[#15803D] inline mr-1" />
            Every delivery attempt is tracked and verified.
          </p>
        </div>
      </section>

      {/* ───────────── SECTION 8 — Reporting ───────────── */}
      <section className="py-24 px-5 sm:px-6" data-testid="section-reporting">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center tracking-tight mb-4">
            Reporting For Managers And Trustees
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Access meaningful operational information without relying on spreadsheets.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ["Campaign Performance", Target],
              ["Fundraiser Performance", Users],
              ["Gift Aid Performance", TrendingUp],
              ["Compliance Status", ShieldCheck],
              ["Deployment Readiness", Compass],
              ["Operational Risks", Gauge],
            ].map(([t, Ic], i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md hover:border-emerald-300 transition-all"
                data-testid={`report-card-${i}`}>
                <Ic className="h-6 w-6 text-[#15803D] mb-3" />
                <div className="font-heading font-bold text-lg">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── SECTION 9 — Built For ───────────── */}
      <section className="py-20 px-5 sm:px-6 bg-slate-900 text-white" data-testid="section-built-for">
        <div className="max-w-5xl mx-auto text-center">
          <Building2 className="h-9 w-9 text-emerald-400 mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-10">
            Built For Organisations That Need Visibility
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
            {[
              "Charity fundraising teams",
              "Face-to-face fundraising agencies",
              "Multi-location operations",
              "Distributed field teams",
              "Compliance-sensitive organisations",
            ].map((t, i) => (
              <div key={i} className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 text-sm font-medium"
                data-testid={`audience-${i}`}>
                {t}
              </div>
            ))}
          </div>
          <p className="text-slate-300 max-w-2xl mx-auto">
            Everything needed to manage fundraising operations from acquisition through reporting.
          </p>
        </div>
      </section>

      {/* ───────────── FINAL CTA ───────────── */}
      <section className="py-24 px-5 sm:px-6 bg-emerald-50" data-testid="section-final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Ready To Simplify Fundraising Operations?
          </h2>
          <p className="text-slate-700 text-lg mb-8 max-w-xl mx-auto">
            Book a demonstration and see how Engage helps organisations gain visibility
            across campaigns, people, donors and outcomes.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button onClick={() => openIntent("demo")} data-testid="final-book-demo-btn"
              className="bg-[#15803D] hover:bg-[#166534] text-white px-7 py-6 text-base font-semibold">
              Book A Demo <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button onClick={() => openIntent("pilot")} variant="outline" data-testid="final-request-pilot-btn"
              className="px-7 py-6 text-base font-semibold border-slate-300">
              Request A Pilot
            </Button>
          </div>
        </div>
      </section>

      {/* ───────────── Footer ───────────── */}
      <footer className="py-12 px-5 sm:px-6 border-t border-slate-200 bg-white" data-testid="footer">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-[#15803D] flex items-center justify-center">
              <span className="text-white font-bold text-sm">E</span>
            </div>
            <div>
              <div className="font-semibold tracking-tight">Engage</div>
              <div className="text-xs text-slate-500">Operational Visibility For Fundraising Organisations</div>
            </div>
          </div>
          <div className="text-xs text-slate-500">
            © {new Date().getFullYear()} OCI Systems Ltd. Engage — Powered by OCI. All rights reserved.
          </div>
        </div>
      </footer>

      {/* ───────────── Enquiry Dialog ───────────── */}
      <Dialog open={showEnquiry}
        onOpenChange={(o) => {
          if (!o) { setSubmitted(false); setForm({ name: "", email: "", phone: "", organisationName: "", message: "" }); }
          setShowEnquiry(o);
        }}>
        <DialogContent className="sm:max-w-md" data-testid="enquiry-dialog">
          {submitted ? (
            <div className="text-center py-6" data-testid="enquiry-success">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-xl mb-2">Request received</h3>
              <p className="text-slate-500 mb-6">We&apos;ll be in touch shortly to arrange your session.</p>
              <Button onClick={() => setShowEnquiry(false)} className="bg-[#15803D] hover:bg-[#166534]" data-testid="enquiry-done-btn">
                Done
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold tracking-tight">
                  {intent === "pilot" ? "Request a pilot" : "Book a demo"}
                </DialogTitle>
                <DialogDescription>
                  Tell us about your operation — we&apos;ll get back to you to schedule a working session.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Name <span className="text-red-500">*</span></Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name" data-testid="enquiry-name" />
                </div>
                <div className="space-y-2">
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com" data-testid="enquiry-email" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="Optional" data-testid="enquiry-phone" />
                </div>
                <div className="space-y-2">
                  <Label>Organisation</Label>
                  <Input value={form.organisationName} onChange={e => setForm({ ...form, organisationName: e.target.value })}
                    placeholder="Charity or agency name" data-testid="enquiry-org" />
                </div>
                <div className="space-y-2">
                  <Label>Anything we should know?</Label>
                  <Textarea rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                    placeholder="Team size, current tools, what you're trying to see more clearly…"
                    data-testid="enquiry-message" />
                </div>
                <Button onClick={submit} disabled={submitting}
                  className="w-full bg-[#15803D] hover:bg-[#166534]" data-testid="enquiry-submit-btn">
                  {submitting ? "Submitting…" : (<><Send className="h-4 w-4 mr-2" /> Submit request</>)}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
